import {
  createContext,
  useContext,
  useId,
  useState,
  type ComponentPropsWithoutRef,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { cx } from './internal/cx.js';

interface AccordionContextValue {
  rootId: string;
  isExpanded: (value: string) => boolean;
  toggle: (value: string) => void;
}

const AccordionContext = createContext<AccordionContextValue | null>(null);

interface AccordionItemContextValue {
  itemId: string;
  isExpanded: boolean;
  toggle: () => void;
  contentId: string;
  triggerId: string;
}

const AccordionItemContext = createContext<AccordionItemContextValue | null>(null);

function useAccordionContext(): AccordionContextValue {
  const ctx = useContext(AccordionContext);
  if (!ctx) throw new Error('Accordion components must be used within Accordion.Root');
  return ctx;
}

function useAccordionItemContext(): AccordionItemContextValue {
  const ctx = useContext(AccordionItemContext);
  if (!ctx) throw new Error('Accordion.Item components must be used within Accordion.Item');
  return ctx;
}

interface AccordionRootBaseProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

/** One panel open at a time: opening another closes the first. */
export interface AccordionSingleProps extends AccordionRootBaseProps {
  multiple?: false;
  /** Controlled expanded item id. Pass `false` when none expanded. */
  expanded?: string | false;
  /** Called when expanded state changes. */
  onExpandedChange?: (value: string | false) => void;
  /** Default expanded item when uncontrolled. */
  defaultExpanded?: string | false;
}

/** Any number of panels open at once. */
export interface AccordionMultipleProps extends AccordionRootBaseProps {
  multiple: true;
  /** Controlled expanded item ids. */
  expanded?: readonly string[];
  /** Called with the full set of expanded ids after a panel opens or closes. */
  onExpandedChange?: (value: string[]) => void;
  /** Default expanded items when uncontrolled. */
  defaultExpanded?: readonly string[];
}

export type AccordionRootProps = AccordionSingleProps | AccordionMultipleProps;

function toList(value: string | false | readonly string[] | undefined): string[] | undefined {
  if (value === undefined) return undefined;
  if (value === false) return [];
  return typeof value === 'string' ? [value] : [...value];
}

function AccordionRoot(props: AccordionRootProps): JSX.Element {
  const {
    children,
    className,
    multiple,
    expanded: expandedProp,
    onExpandedChange,
    defaultExpanded,
    ...rest
  } = props;
  const rootId = useId();
  const [internalExpanded, setInternalExpanded] = useState<string[]>(
    () => toList(defaultExpanded) ?? [],
  );
  const expanded = toList(expandedProp) ?? internalExpanded;

  function setExpanded(next: string[]): void {
    if (expandedProp === undefined) setInternalExpanded(next);
    if (multiple) onExpandedChange?.(next);
    else onExpandedChange?.(next[0] ?? false);
  }

  function toggle(value: string): void {
    if (expanded.includes(value)) setExpanded(expanded.filter((v) => v !== value));
    else setExpanded(multiple ? [...expanded, value] : [value]);
  }

  return (
    <AccordionContext.Provider
      value={{ rootId, isExpanded: (value) => expanded.includes(value), toggle }}
    >
      <div className={cx('nrx-accordion', className)} {...rest}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export interface AccordionItemProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
  /** Unique id for this panel within the accordion. */
  value: string;
}

function AccordionItem({ children, className, value, ...props }: AccordionItemProps): JSX.Element {
  const accordion = useAccordionContext();
  const baseId = useId();
  const triggerId = `${baseId}-trigger`;
  const contentId = `${baseId}-content`;
  const isExpanded = accordion.isExpanded(value);

  return (
    <AccordionItemContext.Provider
      value={{
        itemId: value,
        isExpanded,
        toggle: () => accordion.toggle(value),
        contentId,
        triggerId,
      }}
    >
      <div
        className={cx('nrx-accordion__item', className)}
        data-expanded={isExpanded || undefined}
        {...props}
      >
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
}

/**
 * Arrow keys, Home and End move between the headers of ONE accordion (WAI-ARIA accordion
 * pattern). A nested accordion's headers belong to their own root, so they are skipped.
 */
function moveHeaderFocus(event: KeyboardEvent<HTMLButtonElement>, rootId: string): void {
  const headers = Array.from(
    document.querySelectorAll<HTMLButtonElement>(`[data-nrx-accordion-root="${rootId}"]`),
  ).filter((header) => !header.disabled && header.closest('[inert]') == null);
  const index = headers.indexOf(event.currentTarget);
  if (index === -1) return;
  const last = headers.length - 1;
  const target =
    event.key === 'ArrowDown'
      ? headers[index === last ? 0 : index + 1]
      : event.key === 'ArrowUp'
        ? headers[index === 0 ? last : index - 1]
        : event.key === 'Home'
          ? headers[0]
          : event.key === 'End'
            ? headers[last]
            : undefined;
  if (!target) return;
  event.preventDefault();
  target.focus();
}

export interface AccordionSummaryProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
}

function AccordionSummary({
  children,
  className,
  onClick,
  onKeyDown,
  ...props
}: AccordionSummaryProps): JSX.Element {
  const { rootId } = useAccordionContext();
  const { isExpanded, toggle, contentId, triggerId } = useAccordionItemContext();

  return (
    <button
      type="button"
      id={triggerId}
      aria-expanded={isExpanded}
      aria-controls={contentId}
      data-nrx-accordion-root={rootId}
      className={cx('nrx-accordion__summary', className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) toggle();
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented) moveHeaderFocus(event, rootId);
      }}
      {...props}
    >
      {children}
      <span className="nrx-accordion__chevron" aria-hidden="true" />
    </button>
  );
}

export interface AccordionContentProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

function AccordionContent({ children, className, ...props }: AccordionContentProps): JSX.Element {
  const { isExpanded, contentId, triggerId } = useAccordionItemContext();

  return (
    <div
      id={contentId}
      role="region"
      aria-labelledby={triggerId}
      aria-hidden={!isExpanded}
      className={cx(
        'nrx-accordion__content',
        isExpanded && 'nrx-accordion__content--open',
        className,
      )}
      {...(!isExpanded ? { inert: true } : {})}
      {...props}
    >
      <div className="nrx-accordion__content-inner">
        <div className="nrx-accordion__content-body">{children}</div>
      </div>
    </div>
  );
}

/** Expandable panel primitive for stacked sections. Requires `@import '@nrx/ui/theme.css'`. */
export const Accordion = {
  Root: AccordionRoot,
  Item: AccordionItem,
  Summary: AccordionSummary,
  Content: AccordionContent,
};
